import React, { useState, useEffect, useMemo } from 'react';
import CartSlider from '../../Customer/Cart/imgSlider';
import '../../Customer/Cart/cart.css';
import { useDispatch } from 'react-redux';
import { AddToCart, updateCartItem } from '../../../Redux/actions/cartAction';
import { toast } from 'react-toastify';

const resolveNumber = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const resolveStock = (variant, item) => {
  // Try variant-first then fallback to item-level fields
  const picks = [variant?.stock, variant?.availableQty, variant?.qty, item?.stock, item?.availableQty, item?.quantityOnHand, item?.qty];
  for (const p of picks) {
    if (p === 0) return 0;
    const n = parseInt(p, 10);
    if (!Number.isNaN(n)) return n;
  }
  // default when nothing present
  return 0;
};

const getPriceFromVariant = (variant, fallback) => {
  const n = resolveNumber(variant?.salePrice ?? fallback ?? 0);
  return n < 0 ? 0 : n;
};

const CartModal = ({ CartValue, closeModal, isEdit, editingItem, Currency }) => {
  const dispatch = useDispatch();
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [quantity, setQuantity] = useState(1);

  // current effective stock and flags
  const stock = useMemo(() => resolveStock(selectedVariant, CartValue), [selectedVariant, CartValue]);
  // const unlimited = stock < 0;
  const outOfStock = stock === 0;
  // const plusDisabled = !unlimited && stock > 0 && quantity >= stock;

  useEffect(() => {
    if (isEdit && editingItem) {
      setSelectedVariant(editingItem.selectedVariant || null);
      setSpecialInstructions(editingItem.specialInstructions || '');
      setQuantity(Math.max(1, editingItem.quantity || 1));
    } else {
      if (CartValue?.itemMasterVarients?.length > 0) {
        setSelectedVariant(CartValue.itemMasterVarients[0]);
      }
      setQuantity(1);
    }
  }, [isEdit, editingItem, CartValue]);

  const handleVariantSelection = (variant) => {
    setSelectedVariant(variant);
    // Clamp quantity if new variant stock is lower
    const s = resolveStock(variant, CartValue);
    if (s >= 0 && quantity > s) setQuantity(Math.max(1, s));
  };

  const handleSpecialInstructionsChange = (event) => {
    setSpecialInstructions(event.target.value);
  };

  const handleQuantityChange = (increment) => {
    if (increment) {
      // if (outOfStock) return;
      // else if (quantity < stock) {
      setQuantity((prev) => prev + 1);
      // } else {
      //   toast.warn(`Only ${stock} in stock.`);
      // }
    } else {
      setQuantity((prev) => Math.max(1, prev - 1));
    }
  };

  const unitPrice = useMemo(() => getPriceFromVariant(selectedVariant, CartValue?.itemMasterVarients?.[0]?.salePrice), [selectedVariant, CartValue]);

  const calculateTotalAmount = () => (unitPrice * quantity).toFixed(2);

  const handleATC = () => {
    // if (!selectedVariant) {
    //   alert('Please select a variant');
    //   return;
    // }
    // if (outOfStock) {
    //   toast.error('This variant is out of stock.');
    //   return;
    // }
    // if (!unlimited && quantity > stock) {
    //   toast.error(`Quantity exceeds stock. Max available is ${stock}.`);
    //   return;
    // }

    const mergedData = {
      id: selectedVariant.itemNo || selectedVariant.recordId,
      name: CartValue?.itemName || selectedVariant.itemDescription,
      barcode: selectedVariant.barcode || CartValue?.barcode,
      quantity,
      price: unitPrice,
      item: {
        ...CartValue,
        recordId: CartValue?.record_Id ?? CartValue?.recordId,
        itemNo: selectedVariant.itemNo,
        itemName: CartValue?.itemName,
        salePrice: unitPrice,
        vat: selectedVariant.vat,
        taxRate: CartValue?.taxRate,
      },
      selectedVariant,
      specialInstructions,
      discount: isEdit ? editingItem?.discount || 0 : 0,
      discountType: isEdit ? editingItem?.discountType || null : null,
    };

    if (isEdit) {
      dispatch(
        updateCartItem({
          ...mergedData,
          index: editingItem.index,
          itemData: editingItem.itemData,
        })
      );
    } else {
      dispatch(AddToCart(mergedData));
    }

    closeModal(true);
  };

  return (
    <div className="m-0 p-0 pt-5 px-5">
      <div className="row py-lg-3">
        <div className="col-lg-4">
          <CartSlider val={CartValue} />
        </div>
        <div className="col-lg-8 px-lg-5">
          <div className="CartName">{CartValue?.itemName || editingItem?.name}</div>

          {/* Price + Stock line */}
          <div className="d-flex align-items-center justify-content-between mt-2">
            <div className="cartPrice">
              {Currency?.currency?.code} {calculateTotalAmount()}
            </div>
            <div
              className="badge"
              style={{
                borderRadius: 999,
                padding: '4px 10px',
                fontWeight: 700,
                fontSize: 12,
                background: outOfStock ? '#ffe3e3' : '#e6fcf5',
                color: outOfStock ? '#c92a2a' : '#087f5b',
                border: `1px solid ${outOfStock ? '#ffa8a8' : '#96f2d7'}`,
              }}
              // title={outOfStock ? 'Out of stock' : `In stock: `}
            >
              {/* {outOfStock ? 'Out of stock' : `In stock: `} */}
            </div>
          </div>

          {/* Quantity Selection */}
          <div className="py-3">
            <h6>Quantity</h6>
            <div className="d-flex align-items-center gap-3">
              <button className="btn btn-outline-secondary" onClick={() => handleQuantityChange(false)} disabled={quantity <= 1}>
                -
              </button>
              <span className="h5 mb-0">{quantity}</span>
              <button
                className="btn btn-outline-secondary"
                onClick={() => handleQuantityChange(true)}
                // disabled={outOfStock || plusDisabled}
                // title={ ? `Max ${stock}` : 'Increase'}
              >
                +
              </button>
            </div>
            {/* {!unlimited && !outOfStock && <small className="text-muted d-block mt-1">Max allowed: {stock}</small>} */}
          </div>

          {/* Variant Selection */}
          <div className="py-lg-3">
            <h5 className="mt-3">Select Variant</h5>
            {CartValue?.itemMasterVarients?.map((variant) => (
              <div key={variant.recordId} className="form-check d-flex justify-content-between pb-3">
                <div className="gap-3 d-flex">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="variantSelection"
                    checked={selectedVariant?.recordId === variant.recordId}
                    style={{ width: '26.81px', height: '26.81px' }}
                    onChange={() => handleVariantSelection(variant)}
                  />
                  <label className="form-check-label checkItem">
                    {variant.itemDescription} - {Currency?.currency?.code} {variant?.salePrice}
                  </label>
                </div>
                {/* Inline stock hint per variant (optional but useful) */}
                {/* <span className="text-muted" style={{ fontSize: 12 }}>
                  {resolveStock(variant, CartValue) === 0 ? 'Out' : resolveStock(variant, CartValue) < 0 ? '∞' : `Stock: ${resolveStock(variant, CartValue)}`}
                </span> */}
              </div>
            ))}
          </div>

          {/* Special Instructions */}
          <div className="form-group py-3">
            <label className="special pb-3">Special Instructions</label>
            <textarea
              rows="4"
              cols="100"
              className="textarea form-control"
              placeholder="Enter any special instructions..."
              value={specialInstructions}
              onChange={handleSpecialInstructionsChange}
            ></textarea>
          </div>

          {/* Add to Cart Button */}
          <div className="row pt-lg-3">
            <div className="col-lg-12">
              <button
                onClick={handleATC}
                className="w-100 AddBtn"
                // disabled={!selectedVariant || outOfStock}
                title={'Add to Cart'}
              >
                {isEdit ? 'Update Cart' : 'Add to Cart'} - {Currency?.currency?.code} {calculateTotalAmount()}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartModal;
